import * as Page from '../../components/content/page-header';
import * as Button from '../../components/button/button';
import { Icons } from '../../components/icons/icons';
import { PaginatedTable } from '../../components/table/paginated-table';
import { MainContent } from '../../components/layout/content';
import usePageState from '../../hooks/use-page-state';
import { Modes } from '../../types/page-modes';
import { Actions, Content, Dialog, Title } from '../../components/dialog/dialog';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pageable, Query, usePagination } from '../../hooks/use-pagination';
import { CellImageRenderer, CellRenderer } from '../../components/table/columns';
import { Compare, Configuration, DiffedLayer } from './types/configurations';
import { Label, TextInputLarge } from '../../components/forms/input';
import { Select2 } from '../../components/forms/select';
import { FieldValues, useForm } from 'react-hook-form';
import { useDeleteMutation, usePostMutation, usePutMutation } from '../../hooks/use-mutations';
import Download from '../../assets/icons/download.svg';
import Design from '../../assets/icons/design.svg';
import Publish from '../../assets/icons/publish.svg';
import { createJSONFile } from '../../utils/json-file';
import { axiosInstance } from '../../api/api';
import { FileInputLarge } from '../../components/forms/file';
import { alert } from '../../components/toast/toast';
import { usePostFormDataMutation } from '../../hooks/use-form-mutations';
import { useQuery } from '../../hooks/use-query';
import { RecordStatus } from '../../types/record-status';
import { CacheService } from '../../services/cache-service';
import { GoldMaster } from '../../references/tenant';
import { AppSettings } from '../settings/types/app-settings';
import { wait } from '../../utils/time';
import { copyDeletedLayers, diffLayers, DiffStatuses } from '../../utils/diff';

export const Configurations = (): JSX.Element => {
  const navigate = useNavigate();
  const [pageState, setPageState] = usePageState();
  const [query, setQuery] = useState<Query>({});
  const { register, handleSubmit, setValue, reset, formState: { errors }, watch } = useForm();
  const { data, isLoading } = usePagination<Pageable<Configuration>>('/configurations', query);
  const appSettings = useQuery<AppSettings>({ path: '/appsettings' });
  const publishQuery = useQuery({ path: `/map-layers/publish/${pageState.item?.id as string}`, options: { enabled: false } });
  const compareQuery = useQuery<Compare>({ path: `/map-layers/compare/${pageState.item?.id as string}`, options: { enabled: false } });
  const postMutation = usePostMutation<Configuration>({ path: '/configurations', cacheKey: '/configurations' });
  const copyConfigPostMutation = usePostMutation<Configuration>({ path: '/configurations/duplicate', cacheKey: '/configurations' });
  const putMutation = usePutMutation({ path: `/configurations/${pageState.item?.id as string}`, cacheKey: '/configurations' });
  const deleteMutation = useDeleteMutation({ cacheKey: '/configurations' });
  const postFormMutation = usePostFormDataMutation({ cacheKey: '/configurations' });
  const configType = watch('configType');
  const [diffedLayers, setDiffedLayers] = useState<DiffedLayer[]>();

  const onDeleteConfirm = (): void => {
    deleteMutation.mutate({ path: `/configurations/${pageState.item?.id as string}` });
    setPageState(Modes.BROWSE);
  };

  const onSubmit = async (values: FieldValues): Promise<void> => {
    if (pageState.item?.id) {
      setPageState(Modes.BROWSE);
      putMutation.mutate({ name: values.name });
      return;
    }

    try {
      if (configType === ConfigTypes.COPY_CONFIGURATION) {
        await copyConfigPostMutation.mutateAsync({ name: values.name, configId: values.configId });
      } else {
        const result = await postMutation.mutateAsync({ name: values.name });
        if (configType === ConfigTypes.IMPORT_FOLDERS) {
          await postFormMutation.mutateAsync({ path: `/map-layers/tree/import/${result.layerId}?configurationId=${result.id}`, body: { file: values.file } });
        }
      }
    } catch {
      alert.error('Failed to create configuration');
    }
    setPageState(Modes.BROWSE);
  };

  const onUpdate = (item: Configuration): void => {
    reset();
    setPageState(Modes.UPDATE, item);
    for (const key in item) {
      setValue(key, item[key as keyof Configuration]);
    }
  };

  const onCreate = (): void => {
    reset();
    setPageState(Modes.CREATE);
  };

  const onPublish = async (item: Configuration): Promise<void> => {
    setDiffedLayers([]);
    setPageState(Modes.PUBLISH, item);
    await wait(0);
    const result = await compareQuery.refetch();
    if (!result.data?.current) {
      return;
    }

    try {
      const [previousLayers, currentLayers] = copyDeletedLayers(result.data?.previous?.layers, result.data.current.layers);
      const layers = diffLayers(previousLayers, currentLayers);
      setDiffedLayers([layers]);
    } catch(error) {
      console.error('Diff failed with error', error);
    }
  };

  const onPublishConfirm = async (): Promise<void> => {
    await publishQuery.refetch();
    setPageState(Modes.BROWSE);
    alert.success('Configuration published successfully');
  };

  const onDesign = (item: Configuration): void => {
    navigate(`/configurations/${item.id}/contents/${item.layerId}`);
  };

  const onDownload = async (row: Configuration): Promise<void> => {
    try {
      const result = await axiosInstance().get(`/map-layers/tree/${row.layerId}/export?configurationId=${row.id}`);
      createJSONFile((row.name || 'config'), JSON.stringify(result.data));
    } catch(error) {
      console.error(error);
    }
  };

  const columns = [
    { accessor: 'name', Header: 'Name', sort: true, Cell: CellRenderer },
    { accessor: 'configurationId', Header: 'Configuration Id', Cell: CellRenderer },
    { accessor: 'design', Header: 'Design', Cell: (item: any): JSX.Element => {
      const row = item.row.original as Configuration;
      return <CellImageRenderer image={Design} onClick={(): void => onDesign(row)} />;
    } },
    { accessor: 'download', Header: 'Download', Cell: (item: any): JSX.Element => {
      const row = item.row.original as Configuration;
      return <CellImageRenderer image={Download} onClick={(): Promise<void> => onDownload(row)} />;
    } },
    { accessor: 'publish', Header: 'Publish', Cell: (item: any): JSX.Element => {
      const row = item.row.original as Configuration;
      return <CellImageRenderer image={Publish} onClick={(): Promise<void> => onPublish(row)} />;
    } },
    {
      accessor: 'actions',
      Header: '',
      width: 150,
      maxWidth: 150,
      minWidth: 150,
      sort: false,
      Cell: (item: any): JSX.Element => {
        const row = item.row.original as Configuration;
        const className = row.recordStatus === RecordStatus.GM_LOCKED && CacheService.get('pdc.tenant.id') !== GoldMaster ? 'invisible' : '';
        return (
          <div role="cell" className="body-cell new-td new-td-action">
            <span onClick={(): void => onUpdate(row)} className={className}>
              <Icons.Edit className="pointer row-action" />
            </span>
            {row.id !== appSettings.data?.configurationId && (
              <span onClick={(): void => setPageState(Modes.DELETE, row)} className={className}>
                <Icons.Delete className="pointer row-action" />
              </span>
            )}
          </div>
        );
      }
    },
  ];

  return <>
    <Page.Header>
      <Page.Tenant />
      <Page.Title>
        Configurations
        <Button.PrimarySmall onClick={onCreate}>
          <Icons.Add /> Add Configuration
        </Button.PrimarySmall>
      </Page.Title>
      <Page.SubTitle>DisasterAWARE configurations and publishing.</Page.SubTitle>
    </Page.Header>
    <MainContent>
      <PaginatedTable isLoading={isLoading} columns={columns} data={data} onChange={setQuery} />
    </MainContent>
    {(pageState.mode === Modes.CREATE || pageState.mode === Modes.UPDATE) && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Title>Add Configuration</Title>
          <Content>
            <div className="mb-4">
              <Label name="description">Name</Label>
              <TextInputLarge {...register('name', { required: 'You must provide a value for Name.' })} errors={errors} placeholder="Name" autoFocus={pageState.mode === Modes.CREATE} />
            </div>
            {pageState.mode === Modes.CREATE ? (
              <div className="mb-4">
                <Label>Select a created Configuration type</Label>
                <Select2 { ...register('configType') }>
                  <option value={ConfigTypes.EMPTY}>Empty Configuration</option>
                  <option value={ConfigTypes.IMPORT_FOLDERS}>Import Folders Config</option>
                  <option value={ConfigTypes.COPY_CONFIGURATION}>Copy Existing Configuration</option>
                </Select2>
              </div>
            ) : <></>}
            <div className="mb-4">
              {configType === ConfigTypes.IMPORT_FOLDERS && (
                <>
                  <Label>Configuration File</Label>
                  <FileInputLarge { ...register('file', { required: 'You must provide a value for Configuration File.' })} errors={errors} accept="application/json" />
                  <span className="supported-file-format">Supported file format: JSON</span>
                </>
              )}
              {configType === ConfigTypes.COPY_CONFIGURATION && (
                <>
                  <Label>Select Existing Configuration</Label>
                  <Select2 { ...register('configId', { required: 'You must provide a value for Existing Configuration.' })} errors={errors}>
                    <option value={ConfigTypes.EMPTY}>Select Configuration</option>
                    {data?.content?.map(item => {
                      return <option key={item.id} value={item.id}>{item.name}</option>;
                    })}
                  </Select2>
                </>
              )}
            </div>
          </Content>
          <Actions>
            <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)} disabled={postMutation.isLoading || postFormMutation.isLoading}>Cancel</Button.Tertiary>
            <Button.Primary type="submit" disabled={postMutation.isLoading || postFormMutation.isLoading || copyConfigPostMutation.isLoading}>
              {pageState.mode === Modes.CREATE ? <><Icons.Add /> Add Configuration</> : 'Update'}
            </Button.Primary>
          </Actions>
        </form>
      </Dialog>
    )}
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Configuration</Title>
        <Content>Are you sure you want to delete?</Content>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
    {pageState.mode === Modes.PUBLISH && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} className="large-dialog configuration" backdropClose={false}>
        <Title>
          <div className="d-flex justify-content-between">
            <span>Publish</span>
            <Actions>
              <Button.TertiarySmall className="ps-5 pe-5" onClick={(): void => setPageState(Modes.BROWSE)} disabled={publishQuery.isLoading}>Cancel</Button.TertiarySmall>
              <Button.PrimarySmall className="ps-5 pe-5" onClick={onPublishConfirm} disabled={publishQuery.isLoading}>Confirm</Button.PrimarySmall>
            </Actions>
          </div>
        </Title>
        <Content className="configuration-dialog-content">
          <div>
            {compareQuery.isSuccess && !diffedLayers?.length && <div>No changes</div>}
            {diffedLayers?.map?.((item, index: number) => {
              return <Tree key={`${item.id}-${index}`} offset={0} layer={item} />;
            })}
          </div>
        </Content>
      </Dialog>
    )}
  </>;
};

type Props = {
  offset: number;
  layer: DiffedLayer;
};

const Tree = (props: Props): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  return <>
    <div style={{ paddingLeft: `${props.offset * 28}px` }} className="folder-name d-flex justify-content-between">
      <div className={`d-flex align-items-center`}>
        <span className="me-2">
          {props.layer.isHidden ? <Icons.Hidden /> : <Icons.Visible />}
        </span>
        {props.layer.isFolder && <span className="layer-icon-wrapper"><Icons.Folder /></span>}
        {props.layer.isVirtual && <span className="layer-icon-wrapper"><Icons.VirtualLayer /></span>}
        {!props.layer.isVirtual && !props.layer.isFolder && <span className="layer-icon-wrapper"><Icons.Layer /></span>}
        <span className={`${props.layer.status === DiffStatuses.DELETED ? 'line-through' : ''}`}>{props.layer.name || '/'}</span>
        {props.layer.status && (
          <div className={`config-status`}>
            {props.layer.status === DiffStatuses.UPDATED ? 'Changed' : props.layer.status === DiffStatuses.CREATED ? 'New' : 'Deleted'}
          </div>
        )}
      </div>
      {props.layer.status && props.layer.children?.find(item => item.status) && (
        <div onClick={(): void => setOpen(!open)} className="pointer">
          {open ? <Icons.ArrowUp /> : <Icons.ArrowDown />}
        </div>
      )}
    </div>
    {props.layer.status === DiffStatuses.UPDATED && props.layer.history && (
      <div style={{ paddingLeft: `${props.offset * 28 + 35}px` }}>
        <HtmlDiff history={props.layer.history} />
      </div>
    )}
    <div style={{ display: open ? '' : 'none' }}>
    {props.layer.children?.map((item: DiffedLayer, index: number) => {
      return <Tree key={`${item.id}-${index}`} layer={item} offset={props.offset + 1} />;
    })}
    </div>
  </>;
};

type DiffProps = {
  history: {
    currentItem: DiffedLayer,
    prevItem: DiffedLayer
  }
};

const HtmlDiff = (props: DiffProps): JSX.Element => {
  const prev = props.history.prevItem;
  const current = props.history.currentItem;
  const mergedKeys = new Set([...Object.keys(prev), ...Object.keys(current)]);
  const diffElements: JSX.Element[] = [];
  mergedKeys.forEach((key: string) => {
    const prevValue = prev[key as keyof DiffedLayer];
    const currentValue = current[key as keyof DiffedLayer];
    if (prevValue !== currentValue) {
      diffElements.push(
        <>
          {prevValue !== undefined && <div className="prev-config-value">- {[key]}: {prevValue}</div>}
          {currentValue !== undefined && <div className="current-config-value">+ {[key]}: {currentValue}</div>}
        </>
      );
    }
  });

  return <div>
    <div>{'{'}</div>
      {diffElements}
    <div>{'}'}</div>
  </div>;
};

enum ConfigTypes {
  EMPTY = '',
  IMPORT_FOLDERS = 'import_folders',
  COPY_CONFIGURATION = 'copy_configuration'
}
